/**
 * Typed fetchers for `/client/analytics/*`. Shapes mirror
 * `clients/analytics/analytics.controller.js` in the backend; every route
 * answers `{ success, data }` and the fetchers return `data`.
 */
import baseUrl from "../../api/baseUrl";

export type Granularity = "day" | "month";

export interface Period {
  from: string;
  to: string;
  previous: { from: string; to: string };
  granularity?: Granularity;
}

/** Every KPI: the period's value, the previous period's and % change (null when undefined). */
export interface Kpi {
  current: number | null;
  previous: number | null;
  change: number | null;
}

export type KpiMap<K extends string> = Record<K, Kpi>;

export interface SeriesPoint {
  bucket: string;
  [field: string]: number | string | null;
}

export type Envelope<T> = { success: boolean; data: T };

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

export interface HealthInput {
  key: string;
  label: string;
  value: number | null;
  score: number | null;
}

export interface HealthArea {
  key: string;
  label: string;
  weight: number;
  score: number;
  inputs: HealthInput[];
  whatIf?: { inputKey: string; areaScoreIfGood: number | null; scoreIfGood: number | null };
}

export interface HealthInactiveArea {
  key: string;
  label: string;
  reason: string;
}

export interface OverviewHealth {
  score: number | null;
  basedOn: number;
  total: number;
  areas: HealthArea[];
  inactive: HealthInactiveArea[];
  areaSet: string;
  change: number | null;
  asOfDay: string;
  series: { month: string; score: number | null }[];
}

export type AlertSeverity = "critical" | "warning" | "info";

export interface AnalyticsAlert {
  key: string;
  severity: AlertSeverity;
  tab: "overview" | "revenue" | "patients" | "doctors" | "treatments" | "appointments";
  message: string;
}

export interface UnfinishedSummary {
  lines: number;
  value: number;
  patients: number;
}

export interface AtRiskSummary {
  amber: number;
  red: number;
}

export type OverviewKpiKey =
  | "billed"
  | "collected"
  | "collectionRate"
  | "newPatients"
  | "appointments"
  | "noShowRate"
  | "planCompletionRate"
  | "averageBill";

export interface OverviewData {
  period: Period;
  hasData: boolean;
  health: OverviewHealth | null;
  kpis: KpiMap<OverviewKpiKey>;
  alerts: AnalyticsAlert[];
  series: SeriesPoint[];
  unfinished: UnfinishedSummary | null;
  atRisk: AtRiskSummary | null;
  benchmark: null;
}

/* ------------------------------------------------------------------ */
/* Revenue                                                             */
/* ------------------------------------------------------------------ */

export interface MoneyTotals {
  billed: number;
  collected: number;
  cash: number;
  card: number;
  bankTransfer: number;
  online: number;
  discount: number;
  receipts: number;
  dueCreated: number;
  averageBill: number | null;
  collectionRate: number | null;
}

export type RevenueKpiKey = keyof MoneyTotals;

export interface DiscountByStaff {
  employeeId: string | null;
  name: string;
  amount: number;
  receipts: number;
}

export interface UnpaidAgeing {
  b0_30: number;
  b31_60: number;
  b60p: number;
  count0_30: number;
  count31_60: number;
  count60p: number;
  advancesHeld: number;
}

export interface RevenueData {
  period: Period;
  totals: MoneyTotals;
  previous: MoneyTotals;
  kpis: KpiMap<RevenueKpiKey>;
  series: SeriesPoint[];
  discountByStaff: DiscountByStaff[];
  unpaid: UnpaidAgeing | null;
  unpaidAsOf: string | null;
  daysToPaidAvg: number | null;
}

export type UnpaidBucket = "0_30" | "31_60" | "60p";

export interface UnpaidRow {
  treatmentId: string;
  due: number;
  paymentDate: string | null;
  invoiceNo: string | null;
  patientId: string | null;
  patientName?: string;
  regNo?: string;
  mobileNo?: string;
  days: number;
  bucket: UnpaidBucket;
}

export interface UnpaidListData {
  count: number;
  total: number;
  list: UnpaidRow[];
}

/* ------------------------------------------------------------------ */
/* Patients                                                            */
/* ------------------------------------------------------------------ */

export type SourceKey = "pre_booked" | "walk_in" | "other" | "online";

export interface ActiveBasePoint {
  month: string;
  activeBase: number | null;
  gained: number | null;
  lost: number | null;
}

export interface Cohort {
  month: string;
  size: number;
  returned3: number;
  returned6: number;
  returned12: number;
}

export interface DemographicBucket {
  key: string;
  count: number;
}

export interface TopPatient {
  patientId: string;
  collected: number;
  receipts: number;
  fullName?: string;
  regNo?: string;
}

export interface PatientsData {
  period: Period;
  newPatients: Kpi;
  series: SeriesPoint[];
  activeBaseSeries: ActiveBasePoint[];
  sources: KpiMap<SourceKey>;
  cohorts: Cohort[];
  atRisk: AtRiskSummary | null;
  asOfDay: string | null;
  demographics: { gender: DemographicBucket[]; age: DemographicBucket[] };
  topPatients: TopPatient[];
}

export type AtRiskLevel = "amber" | "red";

export interface AtRiskPatient {
  _id: string;
  fullName?: string;
  regNo?: string;
  mobileNo?: string;
  whatsapp?: string;
  last_activity_at?: string | null;
  registerdDate?: string | null;
  monthsIdle: number | null;
}

export interface AtRiskListData {
  level: AtRiskLevel;
  page: number;
  limit: number;
  count: number;
  list: AtRiskPatient[];
}

/* ------------------------------------------------------------------ */
/* Doctors                                                             */
/* ------------------------------------------------------------------ */

export interface DoctorRow {
  doctorId: string;
  name: string;
  isDentist: boolean | null;
  revenue?: number;
  receipts?: number;
  patients?: number;
  keeps?: number;
  appointments?: number;
  completed?: number;
  noShow?: number;
  linesCreated?: number;
  linesCompleted?: number;
  averageBill?: number | null;
  noShowRate?: number | null;
  completionRate?: number | null;
  previous: {
    revenue: number;
    patients: number;
    appointments: number;
    noShowRate: number | null;
    completionRate: number | null;
  };
  change: { revenue: number | null; patients: number | null };
}

export interface ClinicAverage {
  revenue: number;
  patients: number;
  noShowRate: number | null;
  completionRate: number | null;
}

export interface DoctorSeries {
  doctorId: string;
  name: string;
  points: { month: string; revenue: number; patients: number; completionRate: number | null }[];
}

export interface DoctorsData {
  period: Period;
  doctors: DoctorRow[];
  clinicAverage: ClinicAverage;
  series: DoctorSeries[];
}

/* ------------------------------------------------------------------ */
/* Treatments                                                          */
/* ------------------------------------------------------------------ */

export interface TopTreatment {
  key: string;
  name: string;
  count: number;
  value: number;
  previousCount: number;
  previousValue: number;
  change: number | null;
}

export type PlanKpiKey = "linesCreated" | "valueCreated" | "linesCompleted" | "valueCompleted" | "completionRate";

export interface FunnelStage {
  key: string;
  label: string;
  count: number | null;
  unit?: "%";
}

export interface TreatmentsData {
  period: Period;
  top: TopTreatment[];
  plan: KpiMap<PlanKpiKey>;
  series: SeriesPoint[];
  funnel: FunnelStage[];
  unfinished: UnfinishedSummary | null;
  asOfDay: string | null;
}

export interface UnfinishedPatient {
  patientId: string;
  fullName?: string;
  regNo?: string;
  mobileNo?: string;
  lastVisit?: string | null;
  lines: number;
  value: number;
  invoices: string[];
  treatments: string[];
}

export interface UnfinishedListData {
  page: number;
  limit: number;
  count: number;
  total: number;
  list: UnfinishedPatient[];
}

/* ------------------------------------------------------------------ */
/* Appointments                                                        */
/* ------------------------------------------------------------------ */

export interface AppointmentTotals {
  total: number;
  completed: number;
  noShow: number;
  cancelled: number;
  rescheduled: number;
  bySource: Record<SourceKey, number>;
  noShowRate: number | null;
  cancellationRate: number | null;
  bookedMinutes: number;
  openMinutes: number;
  sessionFill: number | null;
  waitMinutesAvg: number | null;
  leadDaysAvg: number | null;
}

export type AppointmentKpiKey = Exclude<keyof AppointmentTotals, "bySource">;

export interface WeekdayRow {
  weekday: number;
  total: number;
  noShow: number;
  cancelled: number;
  noShowRate: number | null;
}

export interface AppointmentDoctorRow {
  doctorId: string;
  name: string;
  appointments: number;
  completed: number;
  noShow: number;
  noShowRate: number | null;
}

export interface AppointmentsData {
  period: Period;
  totals: AppointmentTotals;
  previous: AppointmentTotals;
  kpis: KpiMap<AppointmentKpiKey>;
  bySource: KpiMap<SourceKey>;
  series: SeriesPoint[];
  heatmap: number[][];
  byWeekday: WeekdayRow[];
  byDoctor: AppointmentDoctorRow[];
  revenuePerOpenHour: number | null;
}

/* ------------------------------------------------------------------ */
/* BookMyDentist (phase 2)                                             */
/* ------------------------------------------------------------------ */

/** `period` without granularity: what the gated (`listed:false` / `enabled:false`) responses carry. */
export type PeriodBare = Omit<Period, "granularity">;

export type ReviewKey = "quality" | "service" | "comfort" | "cleanliness" | "communication";

export type ReviewSub = Record<ReviewKey, number | null>;

export interface ReviewSeriesPoint extends ReviewSub {
  month: string;
  count: number;
  overall: number | null;
}

export interface LatestReview {
  overall: number;
  rating: Partial<Record<ReviewKey, number>> | null;
  comments: string;
  createdAt: string;
}

export interface ProfileItem {
  key: string;
  label: string;
  done: boolean;
}

export interface ProfileChecklist {
  items: ProfileItem[];
  done: number;
  total: number;
  percent: number;
}

/** Previous-period funnel counts, keyed like the funnel stages (`paid` has no stage). */
export interface FunnelPrevious {
  views: number;
  bookingStarts: number;
  booked: number;
  paid: number;
  showed: number;
}

export interface BookMyDentistUnlisted {
  listed: false;
  period: PeriodBare;
}

export interface BookMyDentistListed {
  listed: true;
  status: string;
  slug: string;
  period: Period;
  funnel: FunnelStage[];
  funnelPrevious: FunnelPrevious;
  paidOnline: number;
  payAtClinic: number;
  showUpRate: number | null;
  previousShowUpRate: number | null;
  series: SeriesPoint[];
  onlineRevenue: { collected: number; receipts: number; patients: number };
  reviews: {
    count: number;
    overall: number | null;
    sub: ReviewSub;
    series: ReviewSeriesPoint[];
    latest: LatestReview[];
  };
  profile: ProfileChecklist | null;
}

export type BookMyDentistData = BookMyDentistUnlisted | BookMyDentistListed;

/* ------------------------------------------------------------------ */
/* Inventory (phase 2)                                                 */
/* ------------------------------------------------------------------ */

export interface BelowAlertRow {
  productId: string;
  productName: string;
  availableQuantity: number;
  alertQuantity: number;
  scale?: string | null;
}

export interface DeadStockRow {
  productId: string;
  productName: string;
  availableQuantity: number;
  value: number;
  scale?: string | null;
}

export interface FastMoverRow {
  productId: string;
  productName: string;
  released: number;
  availableQuantity: number;
  scale?: string | null;
}

export interface SupplierRow {
  supplier: string;
  invoices: number;
  total: number;
  due: number;
}

export interface UnpaidSupplierInvoice {
  purchaseId: string;
  invoiceNo?: string | null;
  supplier: string;
  day: string | null;
  total: number;
  due: number;
}

export interface SpendPoint {
  month: string;
  total: number;
}

export interface InventoryDisabled {
  enabled: false;
  period: PeriodBare;
}

/** FIFO cost of what left stock in the period, by release kind. */
export interface CostOfGoods {
  total: number;
  byKind: Partial<Record<"patient" | "employee" | "clinic" | "treatment" | "prescription", number>>;
  uncostedQty: number;
}

/** Patient + staff product sales against their FIFO cost. */
export interface GrossMargin {
  revenue: number;
  cost: number;
  margin: number;
  pct: number | null;
}

export interface InventoryEnabled {
  enabled: true;
  period: PeriodBare;
  /** FIFO: what the units on hand cost (open lots). */
  stockValue: number;
  costOfGoods?: CostOfGoods;
  grossMargin?: GrossMargin;
  products: number;
  inStock: number;
  belowAlert: BelowAlertRow[];
  deadStock: { count: number; value: number; list: DeadStockRow[] };
  fastMovers: FastMoverRow[];
  supplierSpend: { current: number; previous: number; change: number | null };
  bySupplier: SupplierRow[];
  unpaidInvoices: { count: number; total: number; list: UnpaidSupplierInvoice[] };
  spendSeries: SpendPoint[];
}

export type InventoryData = InventoryDisabled | InventoryEnabled;

/* ------------------------------------------------------------------ */
/* Reports (phase 2)                                                   */
/* ------------------------------------------------------------------ */

export type ReportKind = "monthly" | "daily";

export interface ReportListItem {
  /** "2026-09" for monthly, "2026-09-30" for daily. */
  period: string;
  label: string;
  generatedAt: string;
  /** Whether the caller has opened this report. */
  viewed: boolean;
  score: number | null;
  scoreChange: number | null;
  collected: number | null;
  appointments: number | null;
}

export interface ReportListData {
  kind: ReportKind;
  list: ReportListItem[];
}

export type ReportKpiUnit = "Rs" | "%" | "";

export interface ReportKpiRow {
  label: string;
  current: number | null;
  previous: number | null;
  lastYear: number | null;
  change: number | null;
  changeYear: number | null;
  unit: ReportKpiUnit;
  higherIsBetter: boolean;
}

export type ReportKpiKey =
  | "billed"
  | "collected"
  | "collectionRate"
  | "newPatients"
  | "appointments"
  | "noShowRate"
  | "planCompletionRate"
  | "averageBill"
  | "discount";

export interface ReportMove {
  kind: "area" | "kpi";
  key: string;
  label: string;
  delta: number;
  text: string;
}

export interface ReportHealthArea {
  key: string;
  label: string;
  score: number | null;
  previous: number | null;
}

export interface ReportHealth {
  score: number | null;
  previous: number | null;
  change: number | null;
  basedOn: number;
  total: number;
  areas: ReportHealthArea[];
  inactive: HealthInactiveArea[];
}

export interface ReportDoctor {
  doctorId: string;
  name: string;
  revenue: number;
  receipts: number;
  patients: number;
  keeps: number;
  appointments: number;
  completed: number;
  noShow: number;
  linesCreated: number;
  linesCompleted: number;
  averageBill: number | null;
  noShowRate: number | null;
  completionRate: number | null;
}

export interface ReportNextMonth {
  area: string;
  input: string;
  text: string;
}

export interface MonthlyReportPayload {
  month: string;
  label: string;
  health: ReportHealth | null;
  kpis: Record<ReportKpiKey, ReportKpiRow>;
  wins: ReportMove[];
  concerns: ReportMove[];
  doctors: {
    topRevenue: ReportDoctor | null;
    bestCompletion: ReportDoctor | null;
    highestNoShow: ReportDoctor | null;
    all: ReportDoctor[];
  };
  bookmydentist: {
    views: number | null;
    bookingStarts: number | null;
    bookings: number;
    paidOnline: number;
    payAtClinic: number;
    reviews: number;
    ratingAvg: number | null;
  };
  unpaid: UnpaidAgeing | null;
  unfinished: UnfinishedSummary | null;
  atRisk: AtRiskSummary | null;
  nextMonth: ReportNextMonth[];
  daysWithData: number;
}

export interface DailyReportDoctor {
  doctorId: string;
  name: string;
  revenue: number;
  patients: number;
  appointments: number;
  noShow: number;
}

export interface DailyReportTreatment {
  key: string;
  name: string;
  count: number;
  value: number;
}

export interface DailyReportPayload {
  day: string;
  /** True when no daily summary existed for that day; every other field is then absent. */
  empty?: boolean;
  money?: Omit<MoneyTotals, "averageBill" | "collectionRate">;
  patients?: { new: number; seen: number };
  appointments?: { total: number; completed: number; noShow: number; cancelled: number; bySource: Partial<Record<SourceKey, number>> };
  plan?: { linesCreated: number; valueCreated: number; linesCompleted: number; valueCompleted: number } | null;
  online?: { bookings: number; paidOnline: number; payAtClinic: number; reviews: number; ratingSum: number } | null;
  sms?: { sent: number; reminders: number } | null;
  doctors?: DailyReportDoctor[];
  topTreatments?: DailyReportTreatment[];
}

export interface ReportDetail<K extends ReportKind, P> {
  kind: K;
  period: string;
  generatedAt: string;
  payload: P;
}

export type MonthlyReportDetail = ReportDetail<"monthly", MonthlyReportPayload>;
export type DailyReportDetail = ReportDetail<"daily", DailyReportPayload>;

/* ------------------------------------------------------------------ */
/* Fetchers                                                            */
/* ------------------------------------------------------------------ */

export type PeriodQuery = { from: string; to: string };

function get<T>(path: string, params: Record<string, string | number | undefined>): Promise<T> {
  return baseUrl.get<Envelope<T>>(`client/analytics/${path}`, { params }).then((r) => r.data.data);
}

export const getOverview = (p: PeriodQuery) => get<OverviewData>("overview", p);

export const getRevenue = (p: PeriodQuery) => get<RevenueData>("revenue", p);
export const getRevenueUnpaid = (bucket: UnpaidBucket) => get<UnpaidListData>("revenue/unpaid", { bucket });

export const getPatients = (p: PeriodQuery) => get<PatientsData>("patients", p);
export const getPatientsAtRisk = (level: AtRiskLevel, page: number, limit = 20) =>
  get<AtRiskListData>("patients/at-risk", { level, page, limit });

export const getDoctors = (p: PeriodQuery, doctorId?: string) =>
  get<DoctorsData>("doctors", { ...p, doctorId: doctorId || undefined });

export const getTreatments = (p: PeriodQuery) => get<TreatmentsData>("treatments", p);
export const getTreatmentsUnfinished = (page: number, limit = 20) =>
  get<UnfinishedListData>("treatments/unfinished", { page, limit });

export const getAppointments = (p: PeriodQuery) => get<AppointmentsData>("appointments", p);

export const getBookMyDentist = (p: PeriodQuery) => get<BookMyDentistData>("bookmydentist", p);

export const getInventory = (p: PeriodQuery) => get<InventoryData>("inventory", p);

export const getReports = (kind: ReportKind) => get<ReportListData>("reports", { kind });
export const getMonthlyReport = (period: string) => get<MonthlyReportDetail>(`reports/monthly/${encodeURIComponent(period)}`, {});
export const getDailyReport = (period: string) => get<DailyReportDetail>(`reports/daily/${encodeURIComponent(period)}`, {});
